import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { AlertCircle, CheckCircle, Loader, Package, Database, Cloud } from 'lucide-react';

interface DataSourceProgressProps {
    clientId: string;
    sourceType: 'shopify_api' | 'woocommerce_api' | 'feed';
    onComplete?: (success: boolean, summary?: any) => void;
    onError?: (error: string) => void;
}

interface SyncStatus {
    client_id: string;
    source_type: string;
    status: 'syncing' | 'completed' | 'error' | 'failed';
    phase: string;
    message: string;
    products_fetched: number;
    products_total: number;
    products_stored: number;
    products_failed: number;
    embedding_status: string;
    embedding_progress: number;
    progress_percentage: number;
    errors: string[];
    error_count: number;
}

export const DataSourceProgress: React.FC<DataSourceProgressProps> = ({
    clientId,
    sourceType,
    onComplete,
    onError
}) => {
    const [status, setStatus] = useState<SyncStatus | null>(null);
    const [isConnecting, setIsConnecting] = useState(true);
    const [connectionError, setConnectionError] = useState<string | null>(null);
    const eventSourceRef = useRef<EventSource | null>(null);
    const retryCountRef = useRef(0);
    const maxRetries = 3;

    useEffect(() => {
        console.log(`[DataSourceProgress] Starting stream for ${sourceType} - Client: ${clientId}`);
        connectToStream();

        return () => {
            if (eventSourceRef.current) {
                console.log('[DataSourceProgress] Cleaning up EventSource');
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
        };
    }, [clientId, sourceType]);

    const connectToStream = () => {
        // Close existing connection
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }

        setIsConnecting(true);
        setConnectionError(null);

        // Use the new dedicated SSE endpoint
        const streamUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/data-source/${clientId}/stream`;
        console.log(`[DataSourceProgress] Connecting to: ${streamUrl}`);

        try {
            const eventSource = new EventSource(streamUrl);
            eventSourceRef.current = eventSource;

            eventSource.onopen = () => {
                console.log('[DataSourceProgress] Stream connected');
                setIsConnecting(false);
                retryCountRef.current = 0;
            };

            eventSource.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    console.log('[DataSourceProgress] Received update:', data);
                    setStatus(data);

                    // Handle completion
                    if (data.status === 'completed') {
                        console.log('[DataSourceProgress] Sync completed successfully');
                        toast.success(`Successfully imported ${data.products_stored} products`);
                        if (onComplete) {
                            onComplete(true, {
                                processed_products: data.products_stored,
                                source_type: data.source_type
                            });
                        }
                    }

                    // Handle errors
                    if (data.status === 'error' || data.status === 'failed') {
                        console.error('[DataSourceProgress] Sync failed:', data.message);
                        toast.error(data.message || 'Import failed');
                        if (onError) {
                            onError(data.message || 'Import failed');
                        }
                    }
                } catch (error) {
                    console.error('[DataSourceProgress] Failed to parse message:', error);
                }
            };

            eventSource.addEventListener('close', (event: any) => {
                console.log('[DataSourceProgress] Stream closed by server');
                eventSource.close();
                eventSourceRef.current = null;
            });

            eventSource.addEventListener('ping', () => {
                console.log('[DataSourceProgress] Received ping');
            });

            eventSource.addEventListener('error', (event: any) => {
                console.log('[DataSourceProgress] Received error event');
                if (eventSource.readyState === EventSource.CLOSED) {
                    console.log('[DataSourceProgress] Connection closed');
                    handleConnectionError('Connection closed');
                }
            });

            eventSource.onerror = (error) => {
                console.error('[DataSourceProgress] EventSource error:', error);

                if (eventSource.readyState === EventSource.CONNECTING) {
                    console.log('[DataSourceProgress] Reconnecting...');
                    setIsConnecting(true);
                } else if (eventSource.readyState === EventSource.CLOSED) {
                    handleConnectionError('Connection lost');
                }
            };

        } catch (error) {
            console.error('[DataSourceProgress] Failed to create EventSource:', error);
            handleConnectionError('Failed to connect to progress stream');
        }
    };

    const handleConnectionError = (message: string) => {
        console.error(`[DataSourceProgress] Connection error: ${message}`);

        if (retryCountRef.current < maxRetries) {
            retryCountRef.current++;
            console.log(`[DataSourceProgress] Retrying... (${retryCountRef.current}/${maxRetries})`);
            setTimeout(connectToStream, 2000);
        } else {
            setConnectionError(message);
            setIsConnecting(false);
            if (onError) {
                onError(message);
            }
        }
    };

    const getSourceIcon = () => {
        switch (sourceType) {
            case 'shopify_api':
                return <Package className="w-6 h-6" />;
            case 'woocommerce_api':
                return <Database className="w-6 h-6" />;
            case 'feed':
                return <Cloud className="w-6 h-6" />;
            default:
                return <Package className="w-6 h-6" />;
        }
    };

    const getSourceName = () => {
        switch (sourceType) {
            case 'shopify_api':
                return 'Shopify';
            case 'woocommerce_api':
                return 'WooCommerce';
            case 'feed':
                return 'Product Feed';
            default:
                return 'Data Source';
        }
    };

    if (connectionError) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-3">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                    <h3 className="text-lg font-semibold text-red-900">Connection Error</h3>
                </div>
                <p className="text-red-700">{connectionError}</p>
                <button
                    onClick={connectToStream}
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                    Retry Connection
                </button>
            </div>
        );
    }

    if (isConnecting) {
        return (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-center gap-3">
                    <Loader className="w-6 h-6 text-blue-600 animate-spin" />
                    <div>
                        <h3 className="text-lg font-semibold text-blue-900">Connecting...</h3>
                        <p className="text-blue-700 mt-1">Establishing connection to {getSourceName()} import stream</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!status) {
        return (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <div className="flex items-center gap-3">
                    <Loader className="w-6 h-6 text-gray-600 animate-spin" />
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Waiting for data...</h3>
                        <p className="text-gray-700 mt-1">Preparing to import from {getSourceName()}</p>
                    </div>
                </div>
            </div>
        );
    }

    const isComplete = status.status === 'completed';
    const hasError = status.status === 'error' || status.status === 'failed';

    return (
        <div className={`border rounded-lg p-6 ${
            isComplete ? 'bg-green-50 border-green-200' :
            hasError ? 'bg-red-50 border-red-200' :
            'bg-white border-gray-200'
        }`}>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    {getSourceIcon()}
                    <h3 className="text-lg font-semibold">
                        {getSourceName()} Import
                    </h3>
                </div>
                {isComplete && <CheckCircle className="w-6 h-6 text-green-600" />}
                {hasError && <AlertCircle className="w-6 h-6 text-red-600" />}
                {!isComplete && !hasError && <Loader className="w-5 h-5 animate-spin text-blue-600" />}
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
                <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Progress</span>
                    <span className="font-medium">{status.progress_percentage || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                        className={`h-3 rounded-full transition-all duration-500 ${
                            isComplete ? 'bg-green-600' :
                            hasError ? 'bg-red-600' :
                            'bg-blue-600'
                        }`}
                        style={{ width: `${status.progress_percentage || 0}%` }}
                    />
                </div>
            </div>

            {/* Status Information */}
            <div className="space-y-2">
                {/* Current Phase */}
                <div className="flex justify-between">
                    <span className="text-gray-600">Phase:</span>
                    <span className="font-medium capitalize">
                        {status.phase?.replace(/_/g, ' ') || 'Initializing'}
                    </span>
                </div>

                {/* Message */}
                {status.message && (
                    <div className="text-sm text-gray-700 bg-gray-100 rounded p-2">
                        {status.message}
                    </div>
                )}

                {/* Product Stats */}
                {status.products_total > 0 && (
                    <div className="border-t pt-2 mt-2 space-y-1">
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Products Fetched:</span>
                            <span>{status.products_fetched || 0}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Products Stored:</span>
                            <span className="font-medium text-green-600">
                                {status.products_stored || 0} / {status.products_total}
                            </span>
                        </div>
                        {status.products_failed > 0 && (
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Failed:</span>
                                <span className="text-red-600">{status.products_failed}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Embedding Progress */}
                {status.embedding_progress > 0 && (
                    <div className="border-t pt-2 mt-2">
                        <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">Creating Embeddings:</span>
                            <span>{status.embedding_progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="bg-purple-600 h-2 rounded-full transition-all duration-500"
                                style={{ width: `${status.embedding_progress}%` }}
                            />
                        </div>
                    </div>
                )}

                {/* Errors */}
                {status.errors && status.errors.length > 0 && (
                    <div className="border-t pt-2 mt-2">
                        <p className="text-sm font-medium text-red-700 mb-1">Errors:</p>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                            {status.errors.map((error, index) => (
                                <p key={index} className="text-xs text-red-600 bg-red-50 rounded p-1">
                                    {error}
                                </p>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};